import { Toaster as Sonner, toast } from "sonner"
import { CheckCircle2, XCircle, AlertTriangle, Info, Loader2, Sparkles, Target, Award, Bell } from "lucide-react"

const Toaster = ({
  ...props
}) => {
  return (
    <Sonner
      theme="light"
      className="toaster group"
      position="top-right"
      expand={false}
      richColors
      closeButton
      duration={4000}
      gap={8}
      visibleToasts={4}
      toastOptions={{
        style: {
          padding: '14px 18px',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          border: '1px solid',
          maxWidth: '400px',
          backdropFilter: 'blur(8px)',
        },
        classNames: {
          toast: "group toast",
          title: "font-semibold text-[14px]",
          description: "text-[13px] opacity-90 mt-0.5",
          actionButton: "bg-primary text-primary-foreground font-medium px-3 py-1.5 rounded-md text-xs hover:bg-primary/90 transition-colors",
          cancelButton: "bg-muted text-muted-foreground font-medium px-3 py-1.5 rounded-md text-xs",
          closeButton: "bg-white/80 hover:bg-white border border-slate-200 text-slate-500 hover:text-slate-700 rounded-full w-6 h-6",
          success: "!bg-gradient-to-r !from-emerald-50 !to-green-50 !text-emerald-900 !border-emerald-200/80",
          error: "!bg-gradient-to-r !from-red-50 !to-rose-50 !text-red-900 !border-red-200/80",
          warning: "!bg-gradient-to-r !from-amber-50 !to-yellow-50 !text-amber-900 !border-amber-200/80",
          info: "!bg-gradient-to-r !from-blue-50 !to-indigo-50 !text-blue-900 !border-blue-200/80",
        },
      }}
      icons={{
        success: <CheckCircle2 className="w-5 h-5 text-emerald-600" />,
        error: <XCircle className="w-5 h-5 text-red-600" />,
        warning: <AlertTriangle className="w-5 h-5 text-amber-600" />,
        info: <Info className="w-5 h-5 text-blue-600" />,
        loading: <Loader2 className="w-5 h-5 text-slate-600 animate-spin" />,
      }}
      {...props} 
    />
  );
}

// Premium styled toast functions with action-oriented design
const styledToast = {
  success: (message, options = {}) => {
    return toast.success(message, {
      duration: 3500,
      ...options,
      style: {
        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
        borderColor: '#6ee7b7',
        ...options.style,
      },
    });
  },
  error: (message, options = {}) => {
    return toast.error(message, {
      duration: 5000,
      ...options,
      style: {
        background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
        borderColor: '#fca5a5',
        ...options.style,
      },
    });
  },
  warning: (message, options = {}) => {
    return toast.warning(message, {
      duration: 4000,
      ...options,
      style: {
        background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
        borderColor: '#fcd34d',
        ...options.style,
      },
    });
  },
  info: (message, options = {}) => {
    return toast.info(message, {
      duration: 4000,
      ...options,
      style: {
        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
        borderColor: '#93c5fd',
        ...options.style,
      },
    });
  },
  loading: (message, options = {}) => {
    return toast.loading(message, {
      ...options,
    });
  },
  // HR Engine specific contextual toasts
  objective: (message, options = {}) => {
    return toast(message, {
      duration: 4000,
      icon: <Target className="w-5 h-5 text-blue-600" />,
      style: {
        background: 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)',
        borderColor: '#a5b4fc',
      },
      ...options,
    });
  },
  evaluation: (message, options = {}) => {
    return toast(message, {
      duration: 4000,
      icon: <Award className="w-5 h-5 text-purple-600" />,
      style: {
        background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
        borderColor: '#d8b4fe',
      },
      ...options,
    });
  },
  ai: (message, options = {}) => {
    return toast(message, {
      duration: 5000,
      icon: <Sparkles className="w-5 h-5 text-pink-600" />,
      style: {
        background: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)',
        borderColor: '#f9a8d4',
      },
      ...options,
    });
  },
  notification: (message, options = {}) => {
    return toast(message, {
      duration: 4500,
      icon: <Bell className="w-5 h-5 text-amber-600" />,
      style: {
        background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
        borderColor: '#fcd34d',
      },
      ...options,
    });
  },
  promise: toast.promise,
  dismiss: toast.dismiss,
  custom: toast.custom,
};

export { Toaster, toast, styledToast }
